import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { votersAPI } from "../../../services/api";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";

export default function EditVoterEducationInfo({
  isOpen,
  onClose,
  resource, 
  onUpdateResource, 
}) {
  const [resourceType, setResourceType] = useState("pdf");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const queryClient = useQueryClient();
  const [filePreviewUrl, setFilePreviewUrl] = useState("");

  // Pre-fill fields when modal opens
  useEffect(() => {
    if (isOpen && resource) {
      setTitle(resource.title || "");
      setDescription(resource.description || "");
      setResourceType(resource.media?.type || "pdf");
      setLink(resource.media?.link || "");
      setFile(null);
    }
  }, [isOpen, resource]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setFilePreviewUrl("");
    }
  }, [file]);

  const updateMutation = useMutation(
    async ({ id, resourceType, title, description, file, link }) => {
      let payload;
      let headers = {};

      if (resourceType === "pdf") {
        payload = new FormData();
        payload.append("title", title);
        payload.append("description", description);
        if (file) payload.append("file", file);
        headers["Content-Type"] = "multipart/form-data";
      } else {
        payload = {
          title,
          description,
          file_url: link,
        };
      }

      return votersAPI.updateEducationMaterial(id, payload, { headers });
    },
    {
      onSuccess: (res) => {
        const updated = res?.data?.data?.material;
        if (updated) {
          onUpdateResource({
            id: updated.id,
            title: updated.title,
            description: updated.description,
            media: {
              type: updated.file_url?.endsWith(".pdf") ? "pdf" : "link",
              link: updated.file_url,
            },
          });
        }
        queryClient.invalidateQueries("education-materials");
        toast.success("Material updated successfully!");
        onClose();
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || "Failed to update material. Please try again.";
        toast.error(msg);
      },
    }
  );

  const loading = updateMutation.isLoading;

  if (!isOpen) return null;

  const handleUpdate = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title.");
      return;
    }

    if (resourceType === "pdf") {
      if (!file && !resource.media?.link) {
        toast.error("Please upload a PDF file.");
        return;
      }
      if (file) {
        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        if (!isPdf) {
          toast.error("File must be a PDF.");
          return;
        }
        const maxBytes = 10 * 1024 * 1024;
        if (file.size > maxBytes) {
          toast.error("PDF must be 10MB or less.");
          return;
        }
      }
    }

    if (resourceType === "link") {
      if (!link.trim()) {
        toast.error("Please enter a link URL.");
        return;
      }
      try {
        const u = new URL(link.trim());
        if (!/^https?:$/.test(u.protocol)) throw new Error("Invalid protocol");
      } catch {
        toast.error("Please enter a valid http(s) URL.");
        return;
      }
    }

    await updateMutation.mutateAsync({
      id: resource.id,
      resourceType,
      title,
      description,
      file,
      link,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg p-6 relative animate-in slide-in-from-top duration-500 max-h-[90vh] flex flex-col">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
        >
          <XMarkIcon className="w-6 h-6 text-gray-500" />
        </button>

        {/* Header */}
        <h2 className="text-2xl font-bold mb-4">Edit Resource</h2>
        <hr className="-mx-6 mt-4 mb-4 border-t border-gray-300" />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pr-2 flex flex-col gap-4">
          {/* Type selection */}
          <div className="flex gap-4 mt-2">
            {["pdf", "link"].map((type) => (
              <label
                key={type}
                className={`flex items-center gap-2 w-full p-3 border rounded-lg cursor-pointer transition ${
                  resourceType === type
                    ? "border-blue-600 bg-white"
                    : "border-gray-300 bg-white"
                }`}
              >
                <input
                  type="radio"
                  name="resourceType"
                  value={type}
                  className="form-radio text-blue-600"
                  checked={resourceType === type}
                  onChange={() => setResourceType(type)}
                />
                {type === "link" ? "Link" : type.toUpperCase()}
              </label>
            ))}
          </div>

          {/* Title */}
          <div>
            <label className="font-semibold">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              className="mt-1 w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="font-semibold">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              className="mt-1 w-full border border-gray-300 rounded-md p-2"
              rows={4}
            />
          </div>

          {/* PDF Upload */}
          {resourceType === "pdf" && (
            <div>
              <label className="font-semibold mb-2 block">Attachment</label>
              <label className="mb-2 block text-gray-600 font-semibold text-sm">
                Upload New PDF (optional)
              </label>
              <div className="relative mt-1 w-full border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-gray-400">
                {file ? (
                  <>
                    <svg
                      className="w-10 h-10 text-green-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12"
                      />
                    </svg>
                    <p className="text-sm text-green-600 font-medium">
                      Selected: {file.name}
                    </p>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-10 h-10 text-gray-400 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5 5 5M12 5v12"
                      />
                    </svg>
                    <span className="text-blue-600 font-medium">
                      Upload new file
                    </span>
                    <span className="text-gray-700 ml-1">or keep existing</span>
                    {resource.media?.link && (
                      <p className="text-sm mt-2">
                        Current file:{" "}
                        <a
                          href={resource.media.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          {(resource.media.link.split("/").pop() || "PDF").split("?")[0]}
                        </a>
                      </p>
                    )}
                  </>
                )}
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              {(filePreviewUrl || resource.media?.link) && (
                <div className="mt-4">
                  <iframe
                    src={filePreviewUrl || resource.media.link}
                    className="w-full h-96 border rounded-md"
                    title="PDF Preview"
                  />
                </div>
              )}
            </div>
          )}

          {/* Link Input */}
          {resourceType === "link" && (
            <div>
              <label className="font-semibold mb-1 block">URL</label>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://www.sitename.com"
                className="mt-1 w-full border border-gray-300 rounded-md p-2 focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
              />
            </div>
          )}
        </div>

        {/* Divider */}
        <hr className="-mx-6 mt-4 mb-4 border-t border-gray-300" />

        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
}
